/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export type PiiKindName = 'thaiId' | 'card' | 'phone' | 'email' | 'bankAccount';
export type PdpaUnreadReason = 'not-indexed' | 'unsupported' | 'too-large' | 'failed';

export interface PdpaFinding {
  nodeId: string;
  name: string;
  path: string;
  confidentiality?: string;
  source: 'file' | 'ocr';
  counts: Partial<Record<PiiKindName, number>>;
  /** Masked by the sidecar: never a value in full. */
  samples: Partial<Record<PiiKindName, string[]>>;
  total: number;
}

export interface PdpaUnread {
  nodeId: string;
  name: string;
  path: string;
  reason: PdpaUnreadReason;
}

export interface PdpaReport {
  status: 'running' | 'done' | 'cancelled' | 'failed' | 'interrupted';
  startedAt: string;
  finishedAt?: string;
  startedBy: string;
  error?: string;
  total: number;
  scanned: number;
  clean: number;
  documents: Partial<Record<PiiKindName, number>>;
  unreadCounts: Partial<Record<PdpaUnreadReason, number>>;
  findings: PdpaFinding[];
  unread: PdpaUnread[];
}

/**
 * The PDPA scan on the jobs sidecar: which documents hold personal data.
 * Administrators only — the sidecar checks the caller with Alfresco.
 */
@Injectable({ providedIn: 'root' })
export class PdpaService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  private get base(): string {
    return `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/pdpa/scan`;
  }

  /** The scan running now or the last one; null before the first scan. */
  report(): Observable<PdpaReport | null> {
    return this.http.get<PdpaReport>(this.base, { headers: this.credentials() }).pipe(
      catchError((err) => {
        if (err?.status === 404) {
          return of(null);
        }
        throw err;
      })
    );
  }

  /** Starts a scan; a 409 answer carries the scan that is already running. */
  start(): Observable<PdpaReport> {
    return this.http.post<PdpaReport>(this.base, null, { headers: this.credentials() });
  }

  cancel(): Observable<unknown> {
    return this.http.post(`${this.base}/cancel`, null, { headers: this.credentials() });
  }

  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
